// Parsing and formatting vital sign values typed by a nurse (plan §5.1).
//
// A value is a non-negative decimal with at most the vital's number of decimal places.
// It becomes a scaled integer (value × 10^decimals), so nothing downstream uses floating
// point. Either "." or "," is accepted as the decimal separator, because both are typed
// on Kenyan keyboards; thousands separators, signs, units and extra decimal places are
// refused.

import type { Result } from '../result.ts';
import { err, ok } from '../result.ts';
import { VITAL_SIGNS, type VitalKey } from './definitions.ts';

export type VitalValueErrorCode =
  | 'vital.empty'
  | 'vital.not_a_number'
  | 'vital.negative'
  | 'vital.too_many_decimals'
  | 'vital.too_large';

/** English descriptions; the API and the web app own the wording shown to a user. */
export const VITAL_VALUE_ERRORS: Record<VitalValueErrorCode, string> = {
  'vital.empty': 'Enter a value, or leave the field empty if it was not taken',
  'vital.not_a_number': 'Enter numbers only, without the unit',
  'vital.negative': 'A measurement cannot be negative',
  'vital.too_many_decimals': 'Too many decimal places for this measurement',
  'vital.too_large': 'The number is too long',
};

/** Digits before the decimal point. Far beyond any measurement; stops overflow. */
const MAX_INTEGER_DIGITS = 6;

export interface VitalValue {
  readonly key: VitalKey;
  /** The value × 10^decimals of the vital. */
  readonly scaled: number;
}

export function parseVitalValue(
  key: VitalKey,
  text: string,
): Result<VitalValue, VitalValueErrorCode> {
  const trimmed = text.trim();
  if (trimmed === '') return err('vital.empty');
  if (trimmed.startsWith('-')) return err('vital.negative');
  const match = /^(\d+)(?:[.,](\d*))?$/.exec(trimmed);
  if (match === null) return err('vital.not_a_number');
  const integerPart = (match[1] ?? '').replace(/^0+(?=\d)/, '');
  // More decimals than the vital takes are refused even when they are zeros: "1,000" could
  // be a thousand typed with a separator, and must never quietly become 1.
  const fraction = match[2] ?? '';
  const { decimals } = VITAL_SIGNS[key];
  if (fraction.length > decimals) return err('vital.too_many_decimals');
  if (integerPart.length > MAX_INTEGER_DIGITS) return err('vital.too_large');
  const scaled = Number(integerPart + fraction.padEnd(decimals, '0'));
  return ok({ key, scaled });
}

/**
 * A value read back from a `numeric(9,3)` column ("37.200") as a scaled integer. The
 * database pads with zeros; anything that isn't a padded value of this vital is a
 * programming error, so this throws rather than returning a Result.
 */
export function scaledFromStored(key: VitalKey, stored: string): number {
  const match = /^(\d+)(?:\.(\d*?)0*)?$/.exec(stored.trim());
  if (match === null) throw new RangeError('Not a stored vital value');
  const parsed = parseVitalValue(key, `${match[1] ?? ''}.${match[2] ?? ''}`);
  if (!parsed.ok) throw new RangeError(`Stored value does not fit the vital: ${parsed.code}`);
  return parsed.value.scaled;
}

/** A scaled value as text with the vital's decimals, e.g. 372 → "37.2", 345 kg×100 → "3.45". */
export function formatVitalValue(key: VitalKey, scaled: number): string {
  if (!Number.isSafeInteger(scaled) || scaled < 0) {
    throw new RangeError('A vital value must be a non-negative whole number of units');
  }
  const { decimals } = VITAL_SIGNS[key];
  if (decimals === 0) return String(scaled);
  const digits = String(scaled).padStart(decimals + 1, '0');
  return `${digits.slice(0, -decimals)}.${digits.slice(-decimals)}`;
}

/** The value with its unit, e.g. "37.2 °C", "120 mmHg". */
export function formatVitalWithUnit(key: VitalKey, scaled: number): string {
  return `${formatVitalValue(key, scaled)} ${VITAL_SIGNS[key].unitDisplay}`;
}

/** The scaled value as a decimal string for a `numeric` column, e.g. "37.2". */
export function vitalValueToDecimalString(key: VitalKey, scaled: number): string {
  return formatVitalValue(key, scaled);
}
